import { Type, type Static } from "@sinclair/typebox";
import { AgentTurnInputSchema } from "./agent-turn.ts";
import { UuidSchema } from "./identifiers.ts";
import {
  RetrievalEvidenceSchema,
  RetrievalSearchTraceResultSchema
} from "./knowledge-retrieval.ts";

export const KNOWLEDGE_QA_SCENARIO_ID = "knowledgeQa" as const;

export const KNOWLEDGE_QA_TOOL_IDS = {
  start: "knowledge.qa.start"
} as const;

export const KNOWLEDGE_QA_PAGE = {
  contextType: "scenario",
  scenarioId: KNOWLEDGE_QA_SCENARIO_ID,
  pathname: "/knowledge",
  page: "knowledgeQa",
  step: "active"
} as const;

export const KnowledgeQaStartInputSchema = Type.Object({}, { additionalProperties: false });

export const KNOWLEDGE_QA_SCHEMA_VERSION = "1.0" as const;

const QueryIdentifierSchema = Type.String({ minLength: 1, maxLength: 256 });
const UsageSchema = Type.Object(
  {
    inputTokens: Type.Integer({ minimum: 0 }),
    outputTokens: Type.Integer({ minimum: 0 }),
    totalTokens: Type.Integer({ minimum: 0 })
  },
  { additionalProperties: false }
);

export const KnowledgeQaDispositionSchema = Type.Union([
  Type.Literal("answer"),
  Type.Literal("clarify"),
  Type.Literal("abstain")
]);

export const KnowledgeQaResultSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_QA_SCHEMA_VERSION),
    queryId: QueryIdentifierSchema,
    disposition: KnowledgeQaDispositionSchema,
    answer: Type.Union([Type.String({ minLength: 1, maxLength: 20_000 }), Type.Null()]),
    reason: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    selectedEvidence: Type.Array(RetrievalEvidenceSchema, { maxItems: 32 }),
    usage: UsageSchema,
    durationMs: Type.Integer({ minimum: 0 })
  },
  { additionalProperties: false }
);

const RetrievalTraceStepSchema = Type.Object(
  {
    kind: Type.Literal("retrieval"),
    purpose: Type.Union([Type.Literal("probe"), Type.Literal("follow_up")]),
    round: Type.Integer({ minimum: 0 }),
    status: Type.Union([Type.Literal("ok"), Type.Literal("no_evidence"), Type.Literal("failed")]),
    evidenceCount: Type.Integer({ minimum: 0 }),
    query: Type.String({ minLength: 1, maxLength: 4096 }),
    retrieval: RetrievalSearchTraceResultSchema
  },
  { additionalProperties: false }
);

const ReflectionTraceStepSchema = Type.Object(
  {
    kind: Type.Literal("reflection"),
    round: Type.Integer({ minimum: 0 }),
    action: Type.Union([
      Type.Literal("answer"),
      Type.Literal("search_knowledge"),
      Type.Literal("clarify"),
      Type.Literal("abstain")
    ]),
    durationMs: Type.Integer({ minimum: 0 }),
    usage: UsageSchema,
    evidenceIds: Type.Array(Type.String({ minLength: 1, maxLength: 256 }), {
      maxItems: 32,
      uniqueItems: true
    }),
    missingAspects: Type.Array(Type.String({ minLength: 1, maxLength: 256 }), { maxItems: 16 })
  },
  { additionalProperties: false }
);

const DeliverTraceStepSchema = Type.Object(
  {
    kind: Type.Literal("deliver"),
    disposition: KnowledgeQaDispositionSchema,
    selectedEvidenceIds: Type.Array(Type.String({ minLength: 1, maxLength: 256 }), {
      maxItems: 32,
      uniqueItems: true
    })
  },
  { additionalProperties: false }
);

export const OpsKnowledgeQueryTraceSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_QA_SCHEMA_VERSION),
    steps: Type.Array(
      Type.Union([RetrievalTraceStepSchema, ReflectionTraceStepSchema, DeliverTraceStepSchema]),
      { maxItems: 32 }
    ),
    truncated: Type.Boolean()
  },
  { additionalProperties: false }
);

export const OpsKnowledgeQueryInputSchema = AgentTurnInputSchema;

export const OpsKnowledgeQueryProgressSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_QA_SCHEMA_VERSION),
    queryId: QueryIdentifierSchema,
    traceId: QueryIdentifierSchema,
    sequence: Type.Integer({ minimum: 1 }),
    phase: Type.Union([
      Type.Literal("selecting_knowledge"),
      Type.Literal("retrieving"),
      Type.Literal("reflecting"),
      Type.Literal("finalizing")
    ]),
    purpose: Type.Optional(Type.Union([Type.Literal("probe"), Type.Literal("follow_up")])),
    round: Type.Optional(Type.Integer({ minimum: 0 })),
    evidenceCount: Type.Optional(Type.Integer({ minimum: 0 }))
  },
  { additionalProperties: false }
);

export const OpsKnowledgeQueryAnswerDeltaSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_QA_SCHEMA_VERSION),
    queryId: QueryIdentifierSchema,
    traceId: QueryIdentifierSchema,
    delta: Type.String({ minLength: 1, maxLength: 4096 })
  },
  { additionalProperties: false }
);

export const OpsKnowledgeQueryResultSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_QA_SCHEMA_VERSION),
    traceId: QueryIdentifierSchema,
    result: KnowledgeQaResultSchema,
    trace: OpsKnowledgeQueryTraceSchema
  },
  { additionalProperties: false }
);

export const OpsKnowledgeQueryErrorSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_QA_SCHEMA_VERSION),
    traceId: QueryIdentifierSchema,
    queryId: Type.Optional(QueryIdentifierSchema),
    code: Type.String({ minLength: 1, maxLength: 128 }),
    message: Type.String({ minLength: 1, maxLength: 1024 }),
    retryable: Type.Boolean()
  },
  { additionalProperties: false }
);

export const DocumentCitationOpenParamsSchema = Type.Object(
  { documentId: UuidSchema },
  { additionalProperties: false }
);

export type KnowledgeQaDisposition = Static<typeof KnowledgeQaDispositionSchema>;
export type KnowledgeQaResultV1 = Static<typeof KnowledgeQaResultSchema>;
export type OpsKnowledgeQueryTraceV1 = Static<typeof OpsKnowledgeQueryTraceSchema>;
export type OpsKnowledgeQueryInputV1 = Static<typeof OpsKnowledgeQueryInputSchema>;
export type OpsKnowledgeQueryProgressV1 = Static<typeof OpsKnowledgeQueryProgressSchema>;
export type OpsKnowledgeQueryAnswerDeltaV1 = Static<typeof OpsKnowledgeQueryAnswerDeltaSchema>;
export type OpsKnowledgeQueryResultV1 = Static<typeof OpsKnowledgeQueryResultSchema>;
export type OpsKnowledgeQueryErrorV1 = Static<typeof OpsKnowledgeQueryErrorSchema>;
